
import { computed, defineComponent, ref, watch, PropType, getCurrentInstance } from 'vue'
import clamp from 'lodash/clamp'
import get from 'lodash/get'
import { Audience, AudienceSettingsSchemaItem, ActivationToEdit } from '../../../../types/GCB2'
import {
    AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST,
    CHANGE_ACTIVATION_TYPES,
} from '../../../../vars/GCB2'
import AudienceNumberInput from '../changeActivation/AudienceSettings/AudienceNumberInput.vue'
import AudienceDateInput from '../changeActivation/AudienceSettings/AudienceDateInput.vue'
import AudienceSettingsBaseInput from '@/components/inputs/AudienceSettingsBaseInput.vue'
import CustomActivatorSelect from '../../../inputs/CustomActivatorSelect.vue'
import CustomActivatorTreeSelect from '../../../inputs/CustomActivatorTreeSelect.vue'
import { declOfNum } from '../../../../utils'
import AudienceTextInput from '../changeActivation/AudienceSettings/AudienceTextInput.vue'
import BaseInputOutlined from '@root/src/components/inputs/BaseInputOutlined.vue'
import SmallHint from '@/components/SmallHint.vue'
import AudienceSchemaInlineFields from '../changeActivation/AudienceSettings/AudienceSchemaInlineFields.vue'
import AudienceSchemaBlockCard from '../changeActivation/AudienceSettings/AudienceSchemaBlockCard.vue'
import { resetAudienceBlockValues } from '../changeActivation/AudienceSettings/audienceBlockChanged'
import { getMassEditClipboardKey, readMassEditClipboard } from '../changeActivation/AudienceSettings/audienceMassEditClipboard'
import { buildMailingAdditionalSettingsGroups } from '../changeActivation/AudienceSettings/audienceMailingSettings'
import AudienceMailingAdditionalSettings from '../changeActivation/AudienceSettings/AudienceMailingAdditionalSettings.vue'
import AudienceInformNote from '../changeActivation/AudienceSettings/AudienceInformNote.vue'
import cfg from '@root/config'

type SchemaItemWithType = {
    item: AudienceSettingsSchemaItem
    type: string | undefined
    inputWidth: number
    list: any
    showTooltip: any
}

export default defineComponent({
    name: 'MassEditAudienceTitleWithParams',
    components: {
        AudienceNumberInput,
        AudienceSettingsBaseInput,
        CustomActivatorSelect,
        CustomActivatorTreeSelect,
        AudienceDateInput,
        AudienceTextInput,
        BaseInputOutlined,
        SmallHint,
        AudienceSchemaInlineFields,
        AudienceSchemaBlockCard,
        AudienceMailingAdditionalSettings,
        AudienceInformNote,
    },
    props: {
        value: { type: Object, required: true },
        audience: { type: Object as PropType<Audience>, required: true },
        lists: { type: Object, required: true },
        mode: { type: String, required: true },
        type: { type: String, required: false },
        massEdit: { type: Boolean, required: false, default: false },
        currentActivation: { type: Object as PropType<ActivationToEdit>, required: false },
    },
    setup(props, { emit }) {
        const root = getCurrentInstance()!.proxy
        const filterValue = ref({})
        const activationType = computed(() => root.$router.currentRoute.query.type)
        const getBooleanSelectList = (schemaItem: AudienceSettingsSchemaItem) => [
            { id: true, name: schemaItem.trueText ?? AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST.trueText },
            { id: false, name: schemaItem.falseText ?? AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST.falseText },
        ]
        const audience = props.audience
        const isParameterized = computed(() => audience?.settings.parameterized)
        const schema = computed(() => {
            if (props.type === 'preview') {
                return audience?.settings.shortSchema
            }
            return audience?.settings.schema
        })
        const mailingAdditionalSettingsGroups = computed(() =>
            props.type === 'preview' ? [] : buildMailingAdditionalSettingsGroups(audience, props.mode),
        )
        const mailingListStart = computed(() => {
            const s = schema.value
            return (Array.isArray(s) ? s.length : 0) + 1
        })
        const informText = computed(() => audience?.settings?.informText || '')
        const deselectAudience = () => emit('select-audience', null)
        const getComponentTypePure = (schemaItem: AudienceSettingsSchemaItem): string | undefined => {
            if (schemaItem.type === 'select' && schemaItem.list === 'service_list') return 'treeSelect'
            if (schemaItem.type === 'select' || schemaItem.type === 'boolSelect') return 'select'
            if (schemaItem.type === 'text') return 'text'
            if (schemaItem.type === 'date') return 'date'
            if (schemaItem.type === 'numberInput') return 'numberInput'
            if (schemaItem.type === 'textInput') return 'textInput'
            if (schemaItem.type === 'block') return 'block'
            if (schemaItem.type === 'popupHint') return 'popupHint'
            if (schemaItem.type === 'dropdownHint') return 'dropdownHint'
            if (schemaItem.type === 'toggleBlock') return 'toggleBlock'
            if (schemaItem.type === 'collapsibleBlock') return 'collapsibleBlock'
            return undefined
        }
        const getInputWidthPure = (item: AudienceSettingsSchemaItem) => clamp(item.width ?? 0, 70, Number.MAX_SAFE_INTEGER)
        const getListPure = (item: AudienceSettingsSchemaItem) => {
            if (item.type === 'boolSelect') return getBooleanSelectList(item)
            if (item.list) return props.lists[item.list as string]
            if (item.customList) return item.customList
            return undefined
        }
        const getShowTooltipPure = (item: AudienceSettingsSchemaItem) => get(item, 'showTooltip')
        const toTypedItem = (item: AudienceSettingsSchemaItem): SchemaItemWithType => ({
            item,
            type: getComponentTypePure(item),
            inputWidth: getInputWidthPure(item),
            list: getListPure(item),
            showTooltip: getShowTooltipPure(item),
        })
        const isToggleBlockVisible = (item: AudienceSettingsSchemaItem, audienceValues: Record<string, any>) => {
            const current = audienceValues[item.toggle as string]
            const expected = item.toggleValue
            if (Array.isArray(expected)) {
                return expected.includes(current)
            }
            return current === expected
        }
        const flattenVisibleItems = (
            items: AudienceSettingsSchemaItem[] | undefined,
            audienceValues: Record<string, any>,
        ): AudienceSettingsSchemaItem[] => {
            if (!Array.isArray(items)) {
                return []
            }
            const result: AudienceSettingsSchemaItem[] = []
            items.forEach((inner) => {
                if (!inner) {
                    return
                }
                if (Array.isArray(inner)) {
                    result.push(...flattenVisibleItems(inner, audienceValues))
                    return
                }
                if (inner.type === 'toggleBlock') {
                    if (isToggleBlockVisible(inner, audienceValues)) {
                        result.push(...flattenVisibleItems(inner.content, audienceValues))
                    }
                    return
                }
                result.push(inner)
            })
            return result
        }
        const schemaWithTypes = computed(() => {
            const s = schema.value
            if (!s || !Array.isArray(s)) return []
            const audienceValues = props.value[audience?.id] || {}
            return s.map((part: any) => {
                const arr = Array.isArray(part) ? part : (part && typeof part.length === 'number' ? Array.prototype.slice.call(part) : [])
                const withTypes = flattenVisibleItems(arr, audienceValues).map(toTypedItem)
                if (part && 'disabled' in part) (withTypes as any).disabled = part.disabled
                return withTypes
            })
        })
        const getComponentType = (schemaItem: AudienceSettingsSchemaItem) => getComponentTypePure(schemaItem)
        const dropdownHintsState = ref<Record<string | number, boolean>>({})
        const isDropdownHintOpen = (id?: string | number) => (id != null ? !!dropdownHintsState.value[id] : false)
        const toggleDropdownHint = (id?: string | number) => {
            if (id == null) {
                return
            }
            const current = dropdownHintsState.value
            const next: Record<string | number, boolean> = { ...current }
            next[id] = !current[id]
            dropdownHintsState.value = next
        }
        const blockItems = computed(() => {
            const result: AudienceSettingsSchemaItem[] = []
            const raw: any = schema.value
            if (!Array.isArray(raw)) {
                return result
            }
            raw.forEach((part: any) => {
                if (Array.isArray(part)) {
                    part.forEach((item: any) => {
                        if (item && item.type === 'block') {
                            result.push(item as AudienceSettingsSchemaItem)
                        }
                    })
                }
            })
            return result
        })
        const getVisibleBlockTypedItems = (blockItem: AudienceSettingsSchemaItem): SchemaItemWithType[] => {
            const audienceValues = props.value[audience?.id] || {}
            return flattenVisibleItems(blockItem.block_schema, audienceValues).map(toTypedItem)
        }
        const resetBlock = (blockItem: AudienceSettingsSchemaItem) => {
            const audienceId = props.audience?.id
            if (!blockItem || audienceId == null) {
                return
            }
            if (!props.value[audienceId]) {
                root.$set(props.value, audienceId, {})
            }
            resetAudienceBlockValues(blockItem, props.value[audienceId], (target, key, val) => {
                root.$set(target, key, val)
            })
        }
        const disableBlock = (blockItem: AudienceSettingsSchemaItem) => {
            if (!blockItem || !blockItem.valueField) {
                return
            }
            if (!props.value[audience?.id]) {
                return
            }
            props.value[audience?.id][blockItem.valueField] = false
        }
        const enabledBlocks = computed({
            get: () =>
                blockItems.value
                    .filter((item: AudienceSettingsSchemaItem) => props.value[audience?.id]?.[item.valueField!])
                    .map((item: AudienceSettingsSchemaItem) => item.valueField),
            set: (selected: string[]) => {
                blockItems.value.forEach((item: AudienceSettingsSchemaItem) => {
                    props.value[audience?.id][item.valueField!] = selected.includes(item.valueField!)
                })
            },
        })
        const blockOpenState = ref<Record<string, boolean>>({})
        const isBlockOpen = (valueField?: string) => {
            if (!valueField) {
                return false
            }
            if (Object.prototype.hasOwnProperty.call(blockOpenState.value, valueField)) {
                return blockOpenState.value[valueField]
            }
            return props.mode === CHANGE_ACTIVATION_TYPES.create
        }
        const toggleBlockOpen = (valueField?: string) => {
            if (!valueField) {
                return
            }
            blockOpenState.value = { ...blockOpenState.value, [valueField]: !isBlockOpen(valueField) }
        }
        const firstEnabledBlockField = computed(() => {
            const enabled = new Set((enabledBlocks.value || []).filter(Boolean) as string[])
            if (!enabled.size) {
                return null
            }
            const raw: any = schema.value
            if (!Array.isArray(raw)) {
                return null
            }
            for (const part of raw) {
                const items = Array.isArray(part) ? part : []
                for (const item of items) {
                    if (item?.type === 'block' && item.valueField && enabled.has(item.valueField)) {
                        return item.valueField
                    }
                }
            }
            return null
        })
        const expandAllBlocks = () => {
            const next: Record<string, boolean> = { ...blockOpenState.value }
            ;(enabledBlocks.value || []).forEach((field) => {
                if (field) {
                    next[field] = true
                }
            })
            blockOpenState.value = next
        }
        const collapseAllBlocks = () => {
            const next: Record<string, boolean> = { ...blockOpenState.value }
            ;(enabledBlocks.value || []).forEach((field) => {
                if (field) {
                    next[field] = false
                }
            })
            blockOpenState.value = next
        }
        const areAllBlocksOpen = computed(() => {
            const fields = (enabledBlocks.value || []).filter(Boolean) as string[]
            return fields.length > 0 && fields.every((field) => isBlockOpen(field))
        })
        const toggleAllBlocks = () => {
            if (areAllBlocksOpen.value) {
                collapseAllBlocks()
            } else {
                expandAllBlocks()
            }
        }
        watch(
            () => (enabledBlocks.value || []).slice(),
            (next: Array<string | undefined>, prev: Array<string | undefined> | undefined) => {
                if (!prev) {
                    return
                }
                next.forEach((field) => {
                    if (field && !prev.includes(field)) {
                        blockOpenState.value = { ...blockOpenState.value, [field]: true }
                    }
                })
            },
        )
        const copy = (valueField: string, list: any[], selectType: 'default' | 'tree', listKey?: string) => {
            const clearName = getMassEditClipboardKey(valueField, listKey)
            const value = props.value[audience?.id][valueField] || []
            let updatedValue = []
            if (selectType === 'tree') {
                const flatList: any[] = []
                list.forEach(el => flatList.push(...el.children))
                updatedValue = flatList.filter(el => value.includes(el.id))
            } else {
                updatedValue = list.filter((el: any) => value.includes(el.id))
            }
            const obj = { name: props.currentActivation?.name, projectId: props.currentActivation?.projectId, value: updatedValue }
            localStorage.setItem('massEdit-' + clearName, JSON.stringify(obj))
            root.$store.dispatch('callNotify', `Скопировано в буфер обмена`)
        }
        const paste = (valueField: string, list: any[], selectType: 'default' | 'tree', listKey?: string) => {
            const string = readMassEditClipboard(valueField, listKey)
            if (string) {
                const json = JSON.parse(string)
                const clearValueNames = json.value.filter((el: any) => el.name).map((el: any) => el.name.toLowerCase())
                if (json.projectId === props.currentActivation?.projectId) {
                    props.value[audience?.id][valueField] = json.value.map((el: any) => el.id)
                } else {
                    if (selectType === 'tree') {
                        const flatList: any[] = []
                        list.forEach((el: any) => flatList.push(...el.children))
                        const clearList = flatList.map((el: any) => ({ ...el, name: el.name.toLowerCase() }))
                        props.value[audience?.id][valueField] = clearList.filter((el: any) => clearValueNames.includes(el.name)).map((el: any) => el.id)
                    } else {
                        const clearList = list.map((el: any) => ({ ...el, name: el.name.toLowerCase() }))
                        props.value[audience?.id][valueField] = clearList.filter((el: any) => clearValueNames.includes(el.name)).map((el: any) => el.id)
                    }
                }
            } else {
                root.$store.dispatch('callNotify', `Нет скопированных данных`)
            }
        }
        return {
            isParameterized,
            schema,
            schemaWithTypes,
            filterValue,
            getBooleanSelectList,
            getComponentType,
            declOfNum,
            deselectAudience,
            informText,
            mailingAdditionalSettingsGroups,
            mailingListStart,
            CHANGE_ACTIVATION_TYPES,
            activationType,
            cfg,
            copy,
            paste,
            blockItems,
            enabledBlocks,
            resetBlock,
            disableBlock,
            isBlockOpen,
            toggleBlockOpen,
            firstEnabledBlockField,
            expandAllBlocks,
            areAllBlocksOpen,
            toggleAllBlocks,
            dropdownHintsState,
            isDropdownHintOpen,
            toggleDropdownHint,
            getVisibleBlockTypedItems,
        }
    },
})
